// Brand + pricing configuration and display transformers.

export const brand = {
  brand_name: "SmartWeb by 360Webconcepts",
  logo_url:
    "https://cdn.mylocalbusiness.io/prod/68f1a9b9999e39545a84ddd3/theme/logo-y01OtUaryv.png",
  favicon_url:
    "https://cdn.mylocalbusiness.io/prod/68f1a9b9999e39545a84ddd3/theme/favicon-A-KT6WadPk.png",
  primary_color: "#000000",
  font: "Roboto",
  border_radius: 10,
  ai_settings: {
    ai_name: "AI Agent",
    logo_url: null,
    prompt_sentence: null,
    geo_agent: { name: null, color: "#000000", logo_url: null },
    reply_agent: { name: null, color: "#000000", logo_url: null },
    share_agent: { name: null, color: "#000000", logo_url: null },
  },
};

export const plans = [
  {
    id: "prod_TGiC4fBH1unXaZ",
    name: "Google Review Gold Plan",
    source: "stripe",
    max_locations: null,
    prices: [
      {
        amount: 599,
        currency: "USD",
        interval: "month",
        lookup_key: "gold_monthly",
      },
    ],
    trial_settings: { active: true, card_required: true, period_days: 7 },
    reputation: {
      features: { enabled: ["review", "request"], disabled: ["employee"] },
      review_sites: {
        enabled: ["yelp", "booking", "tripadvisor", "airbnb", "facebook"],
        disabled: ["trustpilot", "zillow", "bbb"],
      },
    },
    visibility: {
      features: { enabled: ["listing", "widget"], disabled: ["website"] },
      listings: { enabled: ["google"], disabled: ["apple_maps"] },
    },
    engagement: {
      features: { enabled: ["social"], disabled: [] },
      social_platforms: {
        enabled: ["facebook", "instagram"],
        disabled: ["tiktok", "linkedin"],
      },
    },
  },
  {
    id: "68f5c9128c71b36eff2495f5",
    name: "Bronze",
    source: "stripe",
    max_locations: 1,
    prices: [
      { amount: 99, currency: "USD", interval: "month", lookup_key: "bronze_monthly" },
    ],
    trial_settings: null,
    reputation: {
      features: { enabled: ["review", "request"], disabled: ["employee"] },
      review_sites: {
        enabled: ["facebook"],
        disabled: ["yelp", "booking", "tripadvisor", "airbnb", "trustpilot", "zillow", "bbb"],
      },
    },
    visibility: {
      features: { enabled: ["listing", "widget"], disabled: ["website"] },
      listings: { enabled: ["google"], disabled: ["apple_maps"] },
    },
    engagement: {
      features: { enabled: [], disabled: ["social"] },
      social_platforms: {
        enabled: ["facebook", "instagram"],
        disabled: ["tiktok", "linkedin"],
      },
    },
  },
  {
    id: "68f5d2278c71b36eff2495f8",
    name: "Lifetime",
    source: "stripe",
    max_locations: 1,
    prices: [
      { amount: 500, currency: "USD", interval: "one_time", lookup_key: "lifetime_onetime" },
    ],
    trial_settings: null,
    reputation: {
      features: { enabled: ["review", "request"], disabled: ["employee"] },
      review_sites: {
        enabled: ["yelp", "booking", "tripadvisor", "airbnb", "trustpilot", "facebook", "zillow", "bbb"],
        disabled: [],
      },
    },
    visibility: {
      features: { enabled: ["listing", "widget"], disabled: ["website"] },
      listings: { enabled: ["google"], disabled: ["apple_maps"] },
    },
    engagement: {
      features: { enabled: ["social"], disabled: [] },
      social_platforms: {
        enabled: ["facebook", "instagram"],
        disabled: ["tiktok", "linkedin"],
      },
    },
  },
];

// ---- Transformers ----
const PLATFORM_MAP = {
  googleplay: "Google Play",
  appstore: "Apple App Store",
  apple_maps: "Apple Maps",
  tripadvisor: "TripAdvisor",
  ratemds: "RateMDs",
  consumeraffairs: "Consumer Affairs",
  bbb: "BBB",
  google: "Google",
  yelp: "Yelp",
  booking: "Booking.com",
  airbnb: "Airbnb",
  trustpilot: "Trustpilot",
  zillow: "Zillow",
  facebook: "Facebook",
  instagram: "Instagram",
  tiktok: "TikTok",
  linkedin: "LinkedIn",
};

export const platformName = (slug) =>
  PLATFORM_MAP[slug] ||
  String(slug || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const FEATURE_MAP = {
  review: "Review Monitoring",
  request: "Automated Review Requests",
  employee: "Employee Performance Tracking",
  listing: "Listings Management",
  website: "AI Website",
  widget: "Review Widgets",
  social: "Social Media Automation",
};

export const featureLabel = (key) =>
  FEATURE_MAP[key] ||
  String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const locationLabel = (max) => {
  if (max == null) return "Unlimited Locations";
  if (max <= 1) return "Single Location";
  if (max <= 10) return "Multi Location";
  return "Enterprise Multi Location";
};

// Summarize the top capabilities enabled across a plan.
export const planCapabilities = (plan) => {
  const keys = [
    ...(plan.reputation?.features?.enabled || []),
    ...(plan.visibility?.features?.enabled || []),
    ...(plan.engagement?.features?.enabled || []),
  ];
  return keys.map(featureLabel);
};

export const planReviewSites = (plan) =>
  (plan.reputation?.review_sites?.enabled || []).map(platformName);

export const planPricing = (plan) => {
  const price = plan.prices?.[0];
  if (!price) {
    // Free products have no prices
    if (plan.source === "free") return { label: "Free", sub: null };
    return { label: "Custom Pricing", sub: null };
  }
  if (price.amount === 0) return { label: "Free", sub: null };
  const symbol = price.currency === "USD" ? "$" : `${price.currency} `;
  const intervalMap = { month: "/mo", year: "/yr", week: "/wk" };
  const sub = price.interval === "one_time" ? "one-time" : intervalMap[price.interval] || `/${price.interval}`;
  return { label: `${symbol}${price.amount}`, sub, lookup_key: price.lookup_key };
};

export const planTrial = (plan) => {
  const t = plan.trial_settings;
  if (!t || !t.active) return null;
  return {
    days: t.period_days,
    cardRequired: t.card_required,
  };
};

export const planCta = (plan) => {
  const trial = planTrial(plan);
  if (trial) return "Start Free Trial";
  const price = plan.prices?.[0];
  if (!price || price.amount === 0 || plan.source === "free") return "Start Free";
  return "Get Started";
};

// Recommend the richest paid plan (with a trial), else first.
export const recommendedPlanId =
  plans.find((p) => p.trial_settings?.active)?.id || plans[0]?.id;
